import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { Filter } from "../types/api/filters.type";
import {
  OrganizationFilterPropertyEnum,
  OrganizationFilterPropertyMap,
  TechSkillsFilterPropertyEnum,
  TechSkillsFilterPropertyMap,
} from "../types/AppTypes";
import { QueryParam } from "../types/api/generic.type";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import {
  RefreshReactQuery,
  TECH_SKILLS_PAGE_SIZE,
} from "../constants/AppConstants";
import { useNotification } from "./notification.hook";
import FiltersService from "../services/api/filters/filters.service";

interface useTechSkillsFilterOptionsProps {
  item: Filter;
  queryParamsTechSkills: QueryParam[];
}

export const useTechSkillsFilterOptions = ({
  item,
  queryParamsTechSkills,
}: useTechSkillsFilterOptionsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: [
        `get-infinite-tech-skills-property-${item.key}`,
        debouncedSearch,
        queryParamsTechSkills,
      ],
      queryFn: ({ pageParam, signal }) =>
        FiltersService.listTechSkillsProperty(
          TechSkillsFilterPropertyMap[item.key as TechSkillsFilterPropertyEnum],
          TECH_SKILLS_PAGE_SIZE,
          pageParam as number,
          [
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
            ...queryParamsTechSkills,
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => {
        const { pageNumber, pageSize, total } = lastPage.pagination;
        return (pageNumber + 1) * pageSize < total ? pageNumber + 1 : undefined;
      },
      staleTime: RefreshReactQuery.MODERATE,
    });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    setSearch,
    search,
  };
};

interface useOrganizationFilterOptionsProps {
  item: Filter;
  queryParamsOrganization: QueryParam[];
}

export const useOrganizationFilterOptions = ({
  item,
  queryParamsOrganization,
}: useOrganizationFilterOptionsProps) => {
  const { notification } = useNotification();

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      `get-organization-property-${item.key}`,
      queryParamsOrganization,
    ],
    queryFn: ({ signal }) =>
      FiltersService.listOrganizationProperty(
        OrganizationFilterPropertyMap[
          item.key as OrganizationFilterPropertyEnum
        ],
        queryParamsOrganization,
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isFetching };
};
